import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowLeft,
  BadgeCheck,
  ChevronRight,
  CircleAlert,
  Copy,
  LoaderCircle,
  Info,
  Network,
  Share2,
  UserCheck,
  UsersRound,
} from "lucide-react";
import { Link, useParams } from "react-router-dom";
import { walletApi } from "../api/wallet";
import { useCopy } from "../i18n/copy";
import { RemoteNoticeDialog } from "../components/RemoteNoticeDialog";

function maskPhone(value: string) {
  const phone = String(value || "");
  if (phone.includes("*")) return phone;
  if (phone.length < 7) return phone;
  return `${phone.slice(0, 4)} *** ${phone.slice(-3)}`;
}

function Intro({ title }: { title: string; copy: string }) {
  const { tr } = useCopy();
  return (
    <>
      <Link className="back-link" to="/account">
        <ArrowLeft /> {tr("Volver a mi cuenta")}
      </Link>
      <header className="page-title-row wallet-title">
        <div>
          <p className="eyebrow">{tr("Comunidad GOP ALA")}</p>
          <h1>{tr(title)}</h1>
        </div>
      </header>
    </>
  );
}

export function TeamPage() {
  const { tr } = useCopy();
  const counts = useQuery({
    queryKey: ["team-count"],
    queryFn: walletApi.teamCount,
  });
  const levels = [
    {
      level: 1,
      count: counts.data?.one || 0,
      title: "Equipo directo",
      copy: "Personas invitadas directamente por ti.",
    },
    {
      level: 2,
      count: counts.data?.two || 0,
      title: "Segundo nivel",
      copy: "Miembros conectados a tu equipo directo.",
    },
    {
      level: 3,
      count: counts.data?.three || 0,
      title: "Tercer nivel",
      copy: "Alcance ampliado de tu comunidad.",
    },
  ];
  const total = levels.reduce((sum, item) => sum + item.count, 0);
  return (
    <main className="page growth-page">
      <Intro
        title="Mi equipo"
        copy="Consulta el tamaño y la actividad de tu comunidad, organizada por nivel."
      />
      <section className="team-hero">
        <span>
          <Network />
        </span>
        <div>
          <p>{tr("Miembros en 3 niveles")}</p>
          <strong>{counts.isLoading ? "—" : total}</strong>
        </div>
        <Link className="button sun" to="/growth/invite">
          <Share2 /> {tr("Invitar")}
        </Link>
      </section>
      {counts.isError && (
        <div className="form-feedback error">
          <CircleAlert /> {tr("No pudimos cargar el resumen del equipo.")}
        </div>
      )}
      <section className="team-levels">
        {levels.map((item) => (
          <Link
            to={`/growth/team/${item.level}`}
            className="team-level-card"
            key={item.level}
          >
            <span className="level-number">0{item.level}</span>
            <div>
              <p className="eyebrow">
                {tr("Nivel")} {item.level}
              </p>
              <h2>{tr(item.title)}</h2>
            </div>
            <strong>
              {counts.isLoading ? (
                <LoaderCircle className="spin" />
              ) : (
                item.count
              )}
            </strong>
            <ChevronRight />
          </Link>
        ))}
      </section>
    </main>
  );
}

export function TeamMembersPage() {
  const params = useParams();
  const { tr } = useCopy();
  const level = Math.min(3, Math.max(1, Number(params.level) || 1));
  const members = useQuery({
    queryKey: ["team-members", level],
    queryFn: () => walletApi.teamMembers(level),
  });
  return (
    <main className="page growth-page">
      <Link className="back-link" to="/growth/team">
        <ArrowLeft /> {tr("Volver al equipo")}
      </Link>
      <header className="page-title-row wallet-title">
        <div>
          <p className="eyebrow">
            {tr("Equipo")} · {tr("Nivel")} {level}
          </p>
          <h1>
            {tr("Miembros del nivel")} {level}
          </h1>
        </div>
      </header>
      <section className="member-list">
        {members.isLoading && (
          <div className="loading-row">
            <LoaderCircle /> {tr("Cargando miembros…")}
          </div>
        )}
        {members.isError && (
          <div className="form-feedback error">
            <CircleAlert /> {tr("No pudimos cargar los miembros.")}
          </div>
        )}
        {members.data?.map((member) => (
          <article className="member-row" key={member.id}>
            <span className="member-avatar">
              <UsersRound />
            </span>
            <div>
              <strong>{maskPhone(member.phone)}</strong>
              <small>
                {tr("Miembro del nivel")} {level}
              </small>
            </div>
            <span
              className={`member-status ${member.is_recharge ? "active" : ""}`}
            >
              {member.is_recharge ? (
                <>
                  <BadgeCheck /> {tr("Activo")}
                </>
              ) : (
                tr("Sin activación")
              )}
            </span>
          </article>
        ))}
        {!members.isLoading && !members.data?.length && (
          <div className="empty-state">
            <UsersRound />
            <h2>{tr("Este nivel aún está vacío")}</h2>
          </div>
        )}
      </section>
    </main>
  );
}

export function InvitePage() {
  const [copied, setCopied] = useState(false);
  const [rulesOpen, setRulesOpen] = useState(false);
  const { tr } = useCopy();
  const invite = useQuery({
    queryKey: ["invite-info"],
    queryFn: walletApi.inviteInfo,
  });
  const link = invite.data?.inviteUrl || "";
  const copy = async () => {
    if (!link) return;
    await navigator.clipboard.writeText(link);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2200);
  };
  const share = async () => {
    if (!link) return;
    if (navigator.share)
      await navigator.share({
        title: tr("Únete a GOP ALA Ecuador"),
        text: tr("Conoce GOP ALA y crea tu cuenta desde mi invitación."),
        url: link,
      });
    else await copy();
  };
  return (
    <main className="page growth-page">
      <Intro
        title="Invitar amigos"
        copy="Comparte tu enlace personal y acompaña a tu comunidad desde el primer paso."
      />
      <div className="invite-layout">
        <section className="invite-card">
          <span className="invite-emblem">
            <Share2 />
          </span>
          <p className="eyebrow light">{tr("Mi enlace personal")}</p>
          <div className="invite-title-row">
            <h2>{tr("Invitar amigos")}</h2>
            <button
              className="icon-action"
              aria-label={tr("Reglas")}
              onClick={() => setRulesOpen(true)}
            >
              <Info />
            </button>
          </div>
          <div className="invite-link">
            <span>
              {invite.isLoading
                ? tr("Consultando enlace…")
                : link || tr("Enlace no disponible")}
            </span>
            <button
              aria-label={tr("Copiar enlace")}
              disabled={!link}
              onClick={copy}
            >
              {copied ? <BadgeCheck /> : <Copy />}
            </button>
          </div>
          {copied && (
            <div className="copy-feedback" role="status">
              {tr("Enlace copiado")}
            </div>
          )}
          <button className="button sun full" disabled={!link} onClick={share}>
            <Share2 /> {tr("Compartir invitación")}
          </button>
        </section>
      </div>
      <RemoteNoticeDialog
        open={rulesOpen}
        type={9}
        onClose={() => setRulesOpen(false)}
      />
    </main>
  );
}
